<template>
  <section class="hearing-devices-form pb-3">

    <div class="d-flex justify-content-between flex-wrap align-items-center">

      <h6 class="text-evollu-gray-light is-bold py-3 mb-0">
        {{title}}
      </h6>

      <div class="d-flex align-items-center justify-content-end">

        <div v-if="hasUnsavedChanges">
          <small class="text-orange is-bold">
            {{$t('clinical.people.anamnesis.unsaved')}}
          </small>
        </div>

        <div v-else-if="latestHearingDevice && latestHearingDevice.updated_at !== null && latestHearingDevice.updated_at !== undefined">
          <small>
            {{$t('clinical.people.anamnesis.updated_at')}}
          </small>
          <small class="is-bold">
            {{$t('clinical.people.anamnesis.updated_by', {date: getExpressiveDate(latestHearingDevice.updated_at), name: latestHearingDevice.updated_by ? latestHearingDevice.updated_by.name : ''})}}
          </small>
        </div>

        <i
          v-if="collapsable"
          class="clickable p-3"
          :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
          @click="isCollapsed = !isCollapsed"
        >
        </i>
      </div>

    </div>

    <div v-if="!isCollapsed" class="mt-3">

      <slot name="beforeForm"></slot>

      <form class="evollu-form" v-if="Array.isArray(value) && value.length > 0">

        <div class="row align-items-start">
          <!-- <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.appointments.hearing_devices.device')}}
          </label> -->
          <div class="col-12 col-xl-11 offset-xl-1">
            <table id="hearing-devices-table" class="table">
            <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col">{{$t('clinical.appointments.hearing_devices.brand')}}</th>
                <th scope="col">{{$t('clinical.appointments.hearing_devices.model')}}</th>
                <th scope="col">{{$t('clinical.appointments.hearing_devices.serial_number')}}</th>
                <th scope="col">{{$t('clinical.appointments.hearing_devices.supply_type')}}</th>
                <th scope="col">{{$t('clinical.appointments.hearing_devices.mold')}}</th>
                <th scope="col">{{$t('clinical.appointments.hearing_devices.obs')}}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(hearingDevice, index) in value" :key="index">
                <th scope="row">{{$t(`clinical.appointments.hearing_devices.${hearingDevice.ear}_abreviatture`)}}</th>
                <td>
                  <va-input
                    :id="`hearing-device-brand-${hearingDevice.ear}`"
                    :placeholder="$t('clinical.appointments.hearing_devices.brand')"
                    :value="hearingDevice.brand"
                    @input="v => updateValue(index, 'brand', v)"
                    :disabled="!editing"
                  />
                </td>

                <td>
                  <va-input
                    :id="`hearing-device-model-${hearingDevice.ear}`"
                    :placeholder="$t('clinical.appointments.hearing_devices.model')"
                    :value="hearingDevice.model"
                    @input="v => updateValue(index, 'model', v)"
                    :disabled="!editing"
                  />
                </td>

                <td>
                  <va-input
                    :id="`hearing-device-serial-number-${hearingDevice.ear}`"
                    :placeholder="$t('clinical.appointments.hearing_devices.serial_number')"
                    :value="hearingDevice.serial_number"
                    @input="v => updateValue(index, 'serial_number', v)"
                    :disabled="!editing"
                  />
                </td>

                <td>
                  <multiselect
                    :value="formOptions.supplyTypeOptions.find(o => o.id === hearingDevice.supply_type_id)"
                    :options="formOptions.supplyTypeOptions"
                    @input="v => updateValue(index, 'supply_type_id', v ? v.id : null)"
                    track-by="id"
                    :customLabel="option => $t(option.name)"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    :placeholder="$t('clinical.appointments.hearing_devices.supply_type')"
                    :allow-empty="true"
                    :multiple="false"
                    :searchable="true"
                    open-direction="bottom"
                    :disabled="!editing"
                    >
                    <span slot="noResult">{{$t('patients.noResults')}}</span>
                    <template slot="clear" v-if="editing">
                      <span class="multiselect__clear" v-if="hearingDevice.supply_type_id" @mousedown.prevent.stop="hearingDevice.supply_type_id = null"></span>
                    </template>
                  </multiselect>
                </td>

                <td>
                  <va-checkbox
                    :id="`hearing-device-mold-${hearingDevice.ear}`"
                    :value="hearingDevice.mold"
                    :isCircle="true"
                    @input="v => updateValue(index, 'mold', v)"
                    :disabled="!editing"
                  />
                </td>

                <td>
                  <va-input
                    :id="`hearing-device-obs-${hearingDevice.ear}`"
                    :placeholder="$t('clinical.appointments.hearing_devices.obs')"
                    :value="hearingDevice.obs"
                    @input="v => updateValue(index, 'obs', v)"
                    :disabled="!editing"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          </div>

        </div>

      </form>

      <div v-if="!(Array.isArray(value) && value.length > 0)" class="ml-2">
        <slot name="noData">
          <span>{{$t('clinical.people.no_data')}}</span>
        </slot>
      </div>
    </div>

  </section>
</template>

<script>
import Errors from '@/utils/StandardErrors'
import Multiselect from 'vue-multiselect'
import * as AnamnesisOptions from './AnamnesisOptions'
import * as _ from 'lodash'

export default {
  props: {
    value: {
      type: Array,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    editing: {
      type: Boolean,
      default: true
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: false,
      default: function () {
        return this.$t('clinical.people.anamnesis.sale_data')
      }
    }
  },
  components: {
    Multiselect
  },
  data: function () {
    return {
      isCollapsed: this.collapsable && this.collapsed,
      formOptions: AnamnesisOptions,
      withHearingDeviceSale: null,
      hasUnsavedChanges: false
    }
  },
  computed: {
    latestHearingDevice: function () {
      if (this.value && Array.isArray(this.value) && this.value.length > 0) {
        let valueCopy = JSON.parse(JSON.stringify(this.value))
        valueCopy.sort((a, b) => {
          if (_.get(a, 'updated_at', null) !== null && _.get(b, 'updated_at', null) === null) {
            return -1
          } else if (_.get(a, 'updated_at', null) === null && _.get(b, 'updated_at', null) !== null) {
            return 1
          } else {
            return this.moment(b.updated_at).diff(a.updated_at)
          }
        })
        return valueCopy[0]
      }
      return null
    }
  },
  methods: {
    getHasUnsavedChanges: function () {
      return this.hasUnsavedChanges
    },
    setHasUnsavedChanges: function (value) {
      this.hasUnsavedChanges = value
    },
    getDefaultValue: function () {
      return {
        brand: null,
        model: null,
        serial_number: null,
        supply_type_id: null,
        mold: null,
        obs: null,
        ear: null
      }
    },
    updateValue: function (index, field, value) {
      let newValue = this.value
      newValue[index][field] = value
      this.$emit('input', newValue)
      this.hasUnsavedChanges = true
    },
    getExpressiveDate: function (value) {
      if (value) {
        if (this.$options.filters.isToday(value)) {
          return this.$t('date_helpers.today')
        } else {
          return this.$options.filters.localTime(value, `DD [${this.$t('date_helpers.day_of_month_separator')}] MMMM [${this.$t('date_helpers.day_of_month_separator')}] YYYY`, '', this.locale)
        }
      }
      return ''
    },
    getNormalizedValue: function () {
      let newValue = null
      if (this.value && Array.isArray(this.value)) {
        newValue = this.value.filter(v => v)
          .map(hearingDevice => {
            return {
              ...this.getDefaultValue(),
              ...hearingDevice
            }
          })

        if (newValue.find(hearingDevice => hearingDevice.ear === 'right') === undefined) {
          newValue.push({
            ...this.getDefaultValue(), ear: 'right'
          })
        }

        if (newValue.find(hearingDevice => hearingDevice.ear === 'left') === undefined) {
          newValue.push({
            ...this.getDefaultValue(), ear: 'left'
          })
        }

        newValue.sort((a, b) => {
          if (a.ear === b.ear) {
            return this.moment(b.updated_at).diff(a.updated_at)
          } else {
            return ('' + b.ear).localeCompare(a.ear)
          }
        })
      } else {
        newValue = [
          {
            ...this.getDefaultValue(), ear: 'right'
          },
          {
            ...this.getDefaultValue(), ear: 'left'
          }
        ]
      }
      return newValue
    }
  },
  watch: {
    collapsed: function () {
      this.isCollapsed = this.collapsable && this.collapsed
    },
    value: {
      handler: function (value) {
        if ((value === null || (Array.isArray(value) && (value.find(hearingDevice => hearingDevice.ear === 'left') === undefined || value.find(hearingDevice => hearingDevice.ear === 'right') === undefined))) && this.editing) {
          this.$emit('input', this.getNormalizedValue())
        }
      }
    },
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$emit('input', this.getNormalizedValue())
        }
      },
      immediate: true
    }
  }
}
</script>